import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, DollarSign, Package, Download, FileText, Search } from 'lucide-react';
import apiService from '../../services/ApiService';

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 600);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 600);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── Design tokens ──────────────────────────────────────────────────────────
const FONT     = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG       = '#F2F2F7';
const INK      = '#1C1C1E';
const MUTED    = '#8E8E93';
const SECONDARY = '#3C3C43';
const BRAND    = '#84CC16';
const SYS_BLUE   = '#007AFF';
const SYS_GREEN  = '#34C759';
const SYS_ORANGE = '#FF9500';

const CARD = {
  background: '#fff',
  borderRadius: 18,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)',
};

const INP_STYLE = {
  padding: '11px 14px',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  outline: 'none',
  background: BG,
  color: INK,
  fontFamily: FONT,
  width: '100%',
  boxSizing: 'border-box',
};

const LBL_STYLE = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  marginBottom: 8,
};

const BTN = {
  padding: '11px 22px',
  background: BRAND,
  border: 'none',
  borderRadius: 12,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 15,
  fontFamily: FONT,
};

function IconBox({ icon: Icon, color }) {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: (color || BRAND) + '22',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={18} color={color || BRAND} />
    </div>
  );
}

function ResultBox({ children }) {
  return (
    <div style={{ marginTop: 20, background: BG, borderRadius: 14, padding: '16px 18px' }}>
      {children}
    </div>
  );
}

function ResultStat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || INK, fontVariantNumeric: 'tabular-nums', fontFamily: FONT }}>{value}</div>
    </div>
  );
}

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Spring Calculator ───────────────────────────────────────────────────────
function SpringCalculator() {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [winds, setWinds]   = useState('');
  const [result, setResult] = useState(null);

  function calculate() {
    const w  = parseFloat(weight);
    const h  = parseFloat(height);
    const wn = parseFloat(winds);
    if (!w || !h || !wn || w <= 0 || h <= 0 || wn <= 0) return;

    const torque = (w * h) / (4 * Math.PI * wn);
    let wireSize = '0.207"';
    let notes    = '';
    if      (torque < 20)  { wireSize = '0.162"'; notes = 'Light duty spring.'; }
    else if (torque < 40)  { wireSize = '0.192"'; notes = 'Standard residential spring.'; }
    else if (torque < 60)  { wireSize = '0.207"'; notes = 'Standard to heavy duty spring.'; }
    else if (torque < 80)  { wireSize = '0.218"'; notes = 'Heavy duty spring.'; }
    else if (torque < 100) { wireSize = '0.234"'; notes = 'Commercial grade spring.'; }
    else                   { wireSize = '0.250"'; notes = 'Heavy commercial spring. Consult supplier.'; }

    const cycleLife = Math.round(wn * 10000 / w);
    setResult({ torque: torque.toFixed(2), wireSize, notes, cycleLife: cycleLife.toLocaleString() });
  }

  return (
    <div style={{ ...CARD, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <IconBox icon={Calculator} />
        <span style={{ fontSize: 15, fontWeight: 700, color: INK, fontFamily: FONT }}>Torsion Spring Calculator</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 18 }}>
        <div>
          <label htmlFor="spring-weight" style={LBL_STYLE}>Door Weight (lbs)</label>
          <input id="spring-weight" type="number" min="1" value={weight} onChange={e => setWeight(e.target.value)} style={INP_STYLE} placeholder="e.g. 150" />
        </div>
        <div>
          <label htmlFor="spring-height" style={LBL_STYLE}>Door Height (ft)</label>
          <input id="spring-height" type="number" min="1" value={height} onChange={e => setHeight(e.target.value)} style={INP_STYLE} placeholder="e.g. 7" />
        </div>
        <div>
          <label htmlFor="spring-winds" style={LBL_STYLE}>Wind Count</label>
          <input id="spring-winds" type="number" min="1" value={winds} onChange={e => setWinds(e.target.value)} style={INP_STYLE} placeholder="e.g. 28" />
        </div>
      </div>

      <button onClick={calculate} style={BTN}>Calculate</button>

      {result && (
        <ResultBox>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 16, marginBottom: result.notes ? 14 : 0 }}>
            <ResultStat label="Spring Torque"       value={`${result.torque} ft-lbs`} color={BRAND} />
            <ResultStat label="Recommended Wire"    value={result.wireSize}            color={INK} />
            <ResultStat label="Est. Cycle Life"     value={`${result.cycleLife} cycles`} color={SYS_BLUE} />
          </div>
          {result.notes && (
            <p style={{ fontSize: 13, color: SECONDARY, marginTop: 12, marginBottom: 4, fontFamily: FONT }}>{result.notes}</p>
          )}
          <p style={{ fontSize: 12, color: MUTED, marginTop: 8, fontFamily: FONT }}>
            Estimate for reference only. Always verify with manufacturer specs.
          </p>
        </ResultBox>
      )}
    </div>
  );
}

// ── Markup / Margin Calculator ──────────────────────────────────────────────
function MarkupCalculator() {
  const [cost,   setCost]   = useState('');
  const [margin, setMargin] = useState('');
  const [result, setResult] = useState(null);

  function calculate() {
    const c = parseFloat(cost);
    const m = parseFloat(margin);
    if (!c || !m || c <= 0 || m <= 0 || m >= 100) return;
    const sellPrice = c / (1 - m / 100);
    const markupPct = ((sellPrice - c) / c) * 100;
    const profit    = sellPrice - c;
    setResult({ sellPrice: sellPrice.toFixed(2), markupPct: markupPct.toFixed(1), profit: profit.toFixed(2) });
  }

  return (
    <div style={{ ...CARD, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <IconBox icon={DollarSign} />
        <span style={{ fontSize: 15, fontWeight: 700, color: INK, fontFamily: FONT }}>Markup / Margin Calculator</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label htmlFor="markup-cost" style={LBL_STYLE}>Cost Price ($)</label>
          <input id="markup-cost" type="number" min="0" step="0.01" value={cost} onChange={e => setCost(e.target.value)} style={INP_STYLE} placeholder="0.00" />
        </div>
        <div>
          <label htmlFor="markup-margin" style={LBL_STYLE}>Desired Margin (%)</label>
          <input id="markup-margin" type="number" min="0" max="99" step="0.1" value={margin} onChange={e => setMargin(e.target.value)} style={INP_STYLE} placeholder="e.g. 40" />
        </div>
      </div>

      <button onClick={calculate} style={BTN}>Calculate</button>

      {result && (
        <ResultBox>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <ResultStat label="Selling Price"  value={`$${result.sellPrice}`} color={BRAND} />
            <ResultStat label="Markup %"       value={`${result.markupPct}%`} color={INK} />
            <ResultStat label="Profit / Unit"  value={`$${result.profit}`}    color={SYS_GREEN} />
          </div>
        </ResultBox>
      )}
    </div>
  );
}

// ── Shipping Calculator ─────────────────────────────────────────────────────
function ShippingCalculator() {
  const [shipWeight, setShipWeight] = useState('');
  const [zone,       setZone]       = useState('3');
  const [result,     setResult]     = useState(null);

  function calculate() {
    const w = parseFloat(shipWeight);
    const z = parseInt(zone);
    if (!w || w <= 0) return;
    const baseRate = 8.50 + (z - 1) * 1.50;
    const perPound = 0.35 + (z - 1) * 0.08;
    let cost = baseRate + (Math.max(0, w - 1) * perPound);
    if (w > 70) cost += (w - 70) * 0.50;
    setResult({ cost: cost.toFixed(2), zone: z, weight: w });
  }

  return (
    <div style={{ ...CARD, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <IconBox icon={Package} />
        <span style={{ fontSize: 15, fontWeight: 700, color: INK, fontFamily: FONT }}>Shipping Cost Estimator</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label htmlFor="ship-weight" style={LBL_STYLE}>Package Weight (lbs)</label>
          <input id="ship-weight" type="number" min="0.1" step="0.1" value={shipWeight} onChange={e => setShipWeight(e.target.value)} style={INP_STYLE} placeholder="e.g. 15" />
        </div>
        <div>
          <label htmlFor="ship-zone" style={LBL_STYLE}>Shipping Zone (1–8)</label>
          <select id="ship-zone" value={zone} onChange={e => setZone(e.target.value)} style={INP_STYLE}>
            {[1,2,3,4,5,6,7,8].map(z => <option key={z} value={z}>Zone {z}</option>)}
          </select>
        </div>
      </div>

      <button onClick={calculate} style={BTN}>Estimate</button>

      {result && (
        <ResultBox>
          <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: FONT }}>
            Estimated Cost — {result.weight} lbs, Zone {result.zone}
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, color: BRAND, fontVariantNumeric: 'tabular-nums', fontFamily: FONT }}>${result.cost}</div>
          <p style={{ fontSize: 12, color: MUTED, marginTop: 10, fontFamily: FONT }}>
            Estimate only. Actual rates vary by carrier, dimensions, and service level.
          </p>
        </ResultBox>
      )}
    </div>
  );
}

// ── CSV export helper ───────────────────────────────────────────────────────
function exportCSV(data, filename, columns) {
  const header = columns.map(c => c.label).join(',');
  const rows   = data.map(row => columns.map(c => {
    const val = row[c.key] ?? '';
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  }).join(','));
  const csv  = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function ToolsPage() {
  const isMobile = useIsMobile();
  const [tab,          setTab]          = useState('calculators');
  const [inventory,    setInventory]    = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [priceSearch,  setPriceSearch]  = useState('');
  const [loading,      setLoading]      = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, txns] = await Promise.all([
        apiService.getInventory().catch(() => []),
        apiService.getTransactions().catch(() => []),
      ]);
      setInventory(inv);
      setTransactions(txns);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredPrices = inventory.filter(i =>
    !priceSearch ||
    i.name.toLowerCase().includes(priceSearch.toLowerCase()) ||
    i.sku?.toLowerCase().includes(priceSearch.toLowerCase())
  );

  function handleExportInventory() {
    exportCSV(inventory, 'premier-inventory.csv', [
      { key: 'sku',          label: 'SKU' },
      { key: 'name',         label: 'Name' },
      { key: 'category',     label: 'Category' },
      { key: 'quantity',     label: 'Quantity' },
      { key: 'minQuantity',  label: 'Min Quantity' },
      { key: 'costPrice',    label: 'Cost Price' },
      { key: 'sellingPrice', label: 'Selling Price' },
      { key: 'supplier',     label: 'Supplier' },
      { key: 'location',     label: 'Location' },
      { key: 'notes',        label: 'Notes' },
    ]);
  }

  function handleExportTransactions() {
    exportCSV(transactions, 'premier-transactions.csv', [
      { key: 'date',          label: 'Date' },
      { key: 'type',          label: 'Type' },
      { key: 'description',   label: 'Description' },
      { key: 'amount',        label: 'Amount' },
      { key: 'category',      label: 'Category' },
      { key: 'paymentMethod', label: 'Payment Method' },
      { key: 'status',        label: 'Status' },
      { key: 'customerName',  label: 'Customer' },
      { key: 'invoiceNumber', label: 'Invoice #' },
      { key: 'notes',         label: 'Notes' },
    ]);
  }

  function handleLowStockReport() {
    const low = inventory.filter(i => i.minQuantity > 0 && i.quantity <= i.minQuantity);
    const lines = [
      'PREMIER GARAGE DOOR SUPPLY',
      'LOW STOCK REPORT',
      `Generated: ${new Date().toLocaleString()}`,
      '='.repeat(50),
      '',
      `Total low stock items: ${low.length}`,
      `Out of stock: ${low.filter(i => i.quantity === 0).length}`,
      '',
      'LOW STOCK ITEMS:',
      '-'.repeat(50),
      ...low.map(i => `${i.sku || 'N/A'} | ${i.name} | Qty: ${i.quantity} | Min: ${i.minQuantity} | Supplier: ${i.supplier || 'N/A'}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'low-stock-report.txt'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleValueReport() {
    const totalCost   = inventory.reduce((s, i) => s + i.quantity * i.costPrice,   0);
    const totalRetail = inventory.reduce((s, i) => s + i.quantity * i.sellingPrice, 0);
    const lines = [
      'PREMIER GARAGE DOOR SUPPLY',
      'INVENTORY VALUE REPORT',
      `Generated: ${new Date().toLocaleString()}`,
      '='.repeat(50),
      '',
      `Total Items: ${inventory.length}`,
      `Total Units: ${inventory.reduce((s, i) => s + i.quantity, 0)}`,
      `Total Cost Value: $${totalCost.toFixed(2)}`,
      `Total Retail Value: $${totalRetail.toFixed(2)}`,
      `Potential Gross Margin: $${(totalRetail - totalCost).toFixed(2)}`,
      '',
      'CATEGORY BREAKDOWN:',
      '-'.repeat(50),
      ...Object.entries(
        inventory.reduce((acc, i) => {
          acc[i.category] = acc[i.category] || { cost: 0, retail: 0, count: 0 };
          acc[i.category].cost   += i.quantity * i.costPrice;
          acc[i.category].retail += i.quantity * i.sellingPrice;
          acc[i.category].count++;
          return acc;
        }, {})
      ).map(([cat, d]) => `${cat}: ${d.count} items | Cost: $${d.cost.toFixed(2)} | Retail: $${d.retail.toFixed(2)}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'inventory-value-report.txt'; a.click();
    URL.revokeObjectURL(url);
  }

  // ── iOS-style segmented tab bar ──
  const TABS = [
    { key: 'calculators', label: 'Calculators' },
    { key: 'prices',      label: 'Price Reference' },
    { key: 'actions',     label: 'Quick Actions' },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG, fontFamily: FONT }}>

      {/* ── iOS pill segmented control ── */}
      <div style={{ flexShrink: 0, padding: '16px 12px 8px', background: BG }}>
        <div style={{ display: 'flex', background: '#E5E5EA', borderRadius: 12, padding: 3 }}>
          {TABS.map(({ key, label }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                style={{
                  flex: 1,
                  padding: '7px 6px',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  background: active ? '#fff' : 'transparent',
                  color: active ? INK : MUTED,
                  boxShadow: active ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  transition: 'background 0.15s, color 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 32px' }}>

        {/* Calculators */}
        {tab === 'calculators' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' }}>
            <SpringCalculator />
            <MarkupCalculator />
            <ShippingCalculator />
          </div>
        )}

        {/* Price Reference */}
        {tab === 'prices' && (
          <div>
            {/* Search box */}
            <div style={{ position: 'relative', maxWidth: 420, marginBottom: 16 }}>
              <Search size={15} color={MUTED} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search by name or SKU…"
                value={priceSearch}
                onChange={e => setPriceSearch(e.target.value)}
                style={{ ...INP_STYLE, paddingLeft: 36 }}
              />
            </div>

            {loading ? (
              <p style={{ color: MUTED, fontFamily: FONT, padding: '20px 0' }}>Loading…</p>
            ) : (
              <div style={{ ...CARD, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT }}>
                  <thead>
                    <tr style={{ background: BG }}>
                      {!isMobile && <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>SKU</th>}
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stock</th>
                      {!isMobile && <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cost</th>}
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrices.length === 0 ? (
                      <tr>
                        <td colSpan={isMobile ? 4 : 6} style={{ textAlign: 'center', padding: 40, color: MUTED, fontFamily: FONT }}>
                          No items found.
                        </td>
                      </tr>
                    ) : filteredPrices.map((i, idx) => (
                      <tr key={i.id} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${BG}` }}>
                        {!isMobile && <td style={{ padding: '10px 12px', color: MUTED, fontFamily: 'monospace', fontSize: 12 }}>{i.sku || '—'}</td>}
                        <td style={{ padding: '10px 12px', fontWeight: 500, color: INK }}>{i.name}</td>
                        <td style={{ padding: '10px 12px', color: SECONDARY }}>{i.category}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          color: i.quantity === 0 ? '#FF3B30' : i.quantity <= i.minQuantity ? SYS_ORANGE : SYS_GREEN }}>
                          {i.quantity}
                        </td>
                        {!isMobile && <td style={{ padding: '10px 12px', color: SECONDARY }}>{fmt(i.costPrice)}</td>}
                        <td style={{ padding: '10px 12px', fontWeight: 700, color: BRAND, fontVariantNumeric: 'tabular-nums' }}>{fmt(i.sellingPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        {tab === 'actions' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {[
              { label: 'Export Inventory to CSV',     desc: `Download all ${inventory.length} inventory items as CSV`,                                         icon: Download,  color: BRAND },
              { label: 'Export Transactions to CSV',  desc: `Download all ${transactions.length} transactions as CSV`,                                         icon: Download,  color: SYS_BLUE },
              { label: 'Low Stock Report',            desc: `Generate report for ${inventory.filter(i => i.minQuantity > 0 && i.quantity <= i.minQuantity).length} low-stock items`, icon: FileText, color: SYS_ORANGE },
              { label: 'Inventory Value Report',      desc: 'Generate full inventory valuation report',                                                        icon: FileText,  color: SYS_GREEN },
            ].map(({ label, desc, icon: Icon, color, action }, idx) => {
              const actions = [handleExportInventory, handleExportTransactions, handleLowStockReport, handleValueReport];
              return (
                <button
                  key={label}
                  onClick={actions[idx]}
                  style={{
                    ...CARD,
                    padding: 20,
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 14,
                    fontFamily: FONT,
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: color + '22',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={18} color={color} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 12, color: MUTED }}>{desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
